/**
 * Based on https://shadcn-country-dropdown.vercel.app/phone-input
 * Modified with improved TypeScript support and focus state handling
 */

'use client';
import { useState, forwardRef, useEffect } from 'react';
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';
import { CircleFlag } from 'react-circle-flags';
import { lookup } from 'country-data-list';
import { z } from 'zod';
import { cn } from '@/lib/utils';

import { GlobeIcon } from 'lucide-react';

export const phoneSchema = z.string().refine(value => {
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
}, 'Invalid phone number');

export const optionalPhoneSchema = z.string().refine(value => {
  // Allow empty strings
  if (!value || value.trim() === '') {
    return true;
  }

  // Allow just country codes (like +1, +44, etc.)
  const countryCodePattern = /^\+\d{1,4}$/;
  if (countryCodePattern.test(value.trim())) {
    return true;
  }

  // For actual phone numbers, validate properly
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
}, 'Invalid phone number');

export type CountryData = {
  alpha2: string;
  alpha3: string;
  countryCallingCodes: string[];
  currencies: string[];
  emoji?: string;
  ioc: string;
  languages: string[];
  name: string;
  status: string;
};

interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onCountryChange?: (data: CountryData | undefined) => void;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  defaultCountry?: string;
  className?: string;
  inline?: boolean;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      onCountryChange,
      onChange,
      value,
      placeholder,
      defaultCountry,
      inline = false,
      ...props
    },
    ref
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [countryData, setCountryData] = useState<CountryData | undefined>();
    const [displayFlag, setDisplayFlag] = useState<string>('');
    const [hasInitialized, setHasInitialized] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (defaultCountry) {
        const newCountryData = lookup.countries({
          alpha2: defaultCountry.toLowerCase(),
        })[0];
        setCountryData(newCountryData);
        setDisplayFlag(defaultCountry.toLowerCase());

        if (
          !hasInitialized &&
          newCountryData?.countryCallingCodes?.[0] &&
          !value
        ) {
          const syntheticEvent = {
            target: {
              value: newCountryData.countryCallingCodes[0],
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange?.(syntheticEvent);
          setHasInitialized(true);
        }
      }
    }, [defaultCountry, onChange, value, hasInitialized]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      // Ensure the value starts with "+"
      if (!newValue.startsWith('+')) {
        // Replace "00" at the start with "+" if present
        if (newValue.startsWith('00')) {
          newValue = '+' + newValue.slice(2);
        } else {
          // Otherwise just add "+" at the start
          newValue = '+' + newValue;
        }
      }

      try {
        const parsed = parsePhoneNumber(newValue);

        if (parsed && parsed.country) {
          // Update flag first
          const countryCode = parsed.country;
          // Force immediate update
          setDisplayFlag(''); // Clear first
          setTimeout(() => {
            setDisplayFlag(countryCode.toLowerCase()); // Then set new value
          }, 0);

          // Update other state
          const countryInfo = lookup.countries({ alpha2: countryCode })[0];
          setCountryData(countryInfo);
          onCountryChange?.(countryInfo);

          // Update input value
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: parsed.number,
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange?.(syntheticEvent);
        } else {
          onChange?.(e);
          setDisplayFlag('');
          setCountryData(undefined);
          onCountryChange?.(undefined);
        }
      } catch (error) {
        console.error('Error parsing phone number:', error);
        onChange?.(e);
        setDisplayFlag('');
        setCountryData(undefined);
        onCountryChange?.(undefined);
      }
    };

    const inputClasses = cn(
      // Layout
      'flex items-center gap-2 relative',
      // Size
      'h-9',
      // Background & Border
      'bg-transparent rounded-md border border-input',
      // Padding
      'pl-3',
      // Effects
      'shadow-xs',
      // States
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      // Typography
      'text-base md:text-sm',
      // Transitions
      'transition-[color,box-shadow]',
      // Custom
      '[interpolate-size:allow-keywords]',
      // Conditional
      inline && 'rounded-l-none w-full',
      className
    );

    return (
      <div
        className={cn(
          inputClasses,
          isFocused && 'border-blue-500 ring-2 ring-blue-500'
        )}
      >
        {!inline && (
          <div className="w-4 h-4 rounded-full shrink-0">
            {displayFlag ? (
              <CircleFlag countryCode={displayFlag} />
            ) : (
              <GlobeIcon size={16} />
            )}
          </div>
        )}
        <input
          ref={ref}
          value={value}
          onChange={handlePhoneChange}
          placeholder={placeholder || 'Enter number'}
          type="tel"
          autoComplete="tel"
          name="phone"
          className={cn(
            // Layout
            'flex w-full min-w-0',
            // Size
            'h-9',
            // Spacing
            'px-3 py-1',
            // Typography
            'text-base md:text-sm leading-none',
            // Background & Border
            'border-none bg-transparent',
            // States
            'outline-none',
            // Transitions
            'transition-[color,box-shadow]',
            // Placeholder
            'placeholder:text-muted-foreground',
            // Selection
            'selection:bg-primary selection:text-primary-foreground',
            // Custom
            '[interpolate-size:allow-keywords]',
            className
          )}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlurCapture={() => {
            setIsFocused(false);
          }}
          {...props}
        />
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
